/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { get } from '../../../services/GenesisApi';
import {
  IBeachSwingAndTrades,
  IBeachSwingAndTradesForDateRange,
  INomsValueBySubTerminals,
} from '../../../types/BeachSwingAndTrades.type';
import {
  GET_BEACH_SWING_AND_TRADES,
  GET_BEACH_SWING_AND_TRADES_BY_DATE_RANGE,
} from '../../../constants/BeachSwingAndTrades';

export interface IBeachSwingAndTradeState {
  loading: boolean;
  beachSwingAndTrades: IBeachSwingAndTrades[];
  beachSwingAndTradesForDateRange: IBeachSwingAndTradesForDateRange[];
  nomsValueBySubTerminals: {
    [key: number]: {
      geminiScheduled: number;
      geminiNominated: number;
    };
  };
}

export const initialState: IBeachSwingAndTradeState = {
  loading: false,
  beachSwingAndTrades: [],
  beachSwingAndTradesForDateRange: [],
  nomsValueBySubTerminals: {},
};

export const getBeachSwingAndTrades = createAsyncThunk(
  'beachSwingAndTrades/getBeachSwingAndTrades',
  async ({ startDate }: { startDate: string }) => {
    return get(`${GET_BEACH_SWING_AND_TRADES}?date=${startDate}`, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getBeachSwingAndTradesForDateRange = createAsyncThunk(
  'beachSwingAndTrades/getBeachSwingAndTradesForDateRange',
  async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
    return get(
      `${GET_BEACH_SWING_AND_TRADES_BY_DATE_RANGE}?startDate=${startDate}&endDate=${endDate}`,
      {}
    )
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

const beachSwingAndTrades = createSlice({
  name: 'beachSwingAndTrades',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        getBeachSwingAndTrades.pending,
        (state: IBeachSwingAndTradeState) => {
          state.loading = true;
          state.beachSwingAndTrades = [];
        }
      )
      .addCase(
        getBeachSwingAndTrades.fulfilled,
        (
          state: IBeachSwingAndTradeState,
          {
            payload,
          }: PayloadAction<{
            beachSwingAndTradeDetails: IBeachSwingAndTrades[];
            nomsValueBySubTerminals: INomsValueBySubTerminals[];
          }>
        ) => {
          state.beachSwingAndTrades = payload.beachSwingAndTradeDetails;
          const nomsValueBySubTerminals = {};
          payload.nomsValueBySubTerminals.forEach((_) => {
            nomsValueBySubTerminals[_.subTerminalId] = {
              geminiScheduled: _.geminiNominated,
              geminiNominated: _.geminiNominated,
            };
          });
          state.nomsValueBySubTerminals = nomsValueBySubTerminals;
          state.loading = false;
        }
      )
      .addCase(
        getBeachSwingAndTrades.rejected,
        (state: IBeachSwingAndTradeState) => {
          state.loading = false;
          state.beachSwingAndTrades = [];
        }
      )
      .addCase(
        getBeachSwingAndTradesForDateRange.pending,
        (state: IBeachSwingAndTradeState) => {
          state.loading = true;
          state.beachSwingAndTradesForDateRange = [];
        }
      )
      .addCase(
        getBeachSwingAndTradesForDateRange.fulfilled,
        (
          state: IBeachSwingAndTradeState,
          { payload }: PayloadAction<IBeachSwingAndTradesForDateRange[]>
        ) => {
          state.beachSwingAndTradesForDateRange = payload;
          state.loading = false;
        }
      )
      .addCase(
        getBeachSwingAndTradesForDateRange.rejected,
        (state: IBeachSwingAndTradeState) => {
          state.loading = false;
          state.beachSwingAndTradesForDateRange = [];
        }
      );
  },
});

export const swingContractActions = beachSwingAndTrades.actions;
export default beachSwingAndTrades.reducer;
